import React, { useContext } from "react";
import {
  PaymentDto,
  StudentPaymentDto,
  Institution,
  AccademicYearDto,
  PublicPaymentDto,
  PublicInstitutionDto,
  PublicAccademicYearDto,
  PublicStudentPaymentDto,
} from "../../api-clients/api";
import { SchoolContext } from "../../contexts/SchoolContext";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { isMobile, isTablet } from "react-device-detect";
import {
  dateToString,
  monthName,
  numberToMadPdf,
  paymentTermTypeToString,
} from "../../helper";
import {
  detail,
  header,
  highlitedColor,
  pdfFooter,
  pdfHeader,
  secondaryColor,
  space,
  subHeader,
  table,
  tableHeader,
  title,
} from "../PDF/PdfLayout";
import { useTranslation } from "react-i18next";

var pdfMake = require("pdfmake/build/pdfmake.js");
var pdfFonts = require("pdfmake/build/vfs_fonts.js");
pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface IProps {
  institution: Institution | PublicInstitutionDto;
  payment: PaymentDto | PublicPaymentDto;
  accademicYear: AccademicYearDto | PublicAccademicYearDto;
  paymentId: string;
}
export default function PaymentPdf({
  institution,
  payment,
  accademicYear,
  paymentId,
}: IProps) {
  const { t } = useTranslation();
  const getMonth = (
    studentPayment: StudentPaymentDto | PublicStudentPaymentDto
  ): string => {
    let result = "";
    let totalAmount = studentPayment.totalAmount;
    if (studentPayment.situation != null) {
      const paidMonths = studentPayment.situation.filter(
        (f) => f.totalPaid > 0
      );
      for (let i = paidMonths.length - 1; i >= 0; i--) {
        if (totalAmount > 0) {
          result = `${monthName(paidMonths[i].month)} ${result.length > 0 ? "| " + result : ""}`;
          totalAmount -= paidMonths[i].totalPaid;
        }
      }
    }
    return result;
  };

  const getPaymentHeadersWidths = (): any[] => {
    var headers: any[] = ["auto", "*", "*"];
    if (accademicYear != null) {
      if (accademicYear.hasTransport) headers.push("*");
      if (accademicYear.hasChildCare) headers.push("*");
      if (accademicYear.hasCanteen) headers.push("*");
    }

    return headers;
  };

  const getPaymentHeaders = (): any[] => {
    var headers: any[] = [
      { text: t("studentPath.student"), ...tableHeader },
      { text: t("labels.inscription"), ...tableHeader },
      { text: t("labels.tuition"), ...tableHeader },
    ];
    if (accademicYear != null) {
      if (accademicYear.hasTransport)
        headers.push({ text: t("studentPath.transport"), ...tableHeader });
      if (accademicYear.hasChildCare)
        headers.push({ text: t("studentPath.childCare"), ...tableHeader });
      if (accademicYear.hasCanteen)
        headers.push({ text: t("studentPath.canteen"), ...tableHeader });
    }

    return headers;
  };

  const getPaymentContent = (
    sp: StudentPaymentDto | PublicStudentPaymentDto
  ): any[] => {
    var content: any[] = [
      [
        {
          text: `${sp.studentFirstName} ${sp.studentLastName}`,
          color: highlitedColor,
        },
        { text: sp.classroomName, ...detail },
      ],
      {
        text:
          sp.inscriptionAmount != null && sp.inscriptionAmount > 0
            ? numberToMadPdf(sp.inscriptionAmount)
            : "-",
        color:
          sp.inscriptionAmount != null && sp.inscriptionAmount > 0
            ? highlitedColor
            : secondaryColor,
      },
      {
        text:
          sp.schoolAmount != null && sp.schoolAmount > 0
            ? numberToMadPdf(sp.schoolAmount)
            : "-",
        color:
          sp.schoolAmount != null && sp.schoolAmount > 0
            ? highlitedColor
            : secondaryColor,
      },
    ];
    if (accademicYear != null) {
      if (accademicYear.hasTransport)
        content.push({
          text:
            sp.transportAmount != null && sp.transportAmount > 0
              ? numberToMadPdf(sp.transportAmount)
              : "-",
          color:
            sp.transportAmount != null && sp.transportAmount > 0
              ? highlitedColor
              : secondaryColor,
        });
      if (accademicYear.hasChildCare)
        content.push({
          text:
            sp.childCareAmount != null && sp.childCareAmount > 0
              ? numberToMadPdf(sp.childCareAmount)
              : "-",
          color:
            sp.childCareAmount != null && sp.childCareAmount > 0
              ? highlitedColor
              : secondaryColor,
        });
      if (accademicYear.hasCanteen)
        content.push({
          text:
            sp.canteenAmount != null && sp.canteenAmount > 0
              ? numberToMadPdf(sp.canteenAmount)
              : "-",
          color:
            sp.canteenAmount != null && sp.canteenAmount > 0
              ? highlitedColor
              : secondaryColor,
        });
    }

    return content;
  };

  const content = [
    pdfHeader(
      institution?.logo ?? "",
      [200, institution?.receiptFormat === "A4" ? 140 : 70],
      institution?.printReceiptNumber
        ? t("payments.receiptNumber", { number: payment.paymentNumber })
        : "",
      institution.printReceiptQrCode
        ? `https://${window.location.hostname}/paymentReceipt/${paymentId}`
        : null
    ),
    { ...space },
    { text: t("pdf.paymentDetail"), ...title },
    {
      ...table,
      table: {
        headerRows: 1,
        widths: [...getPaymentHeadersWidths()],
        body: [
          [...getPaymentHeaders()],
          ...((payment.studentPayments as StudentPaymentDto[])?.map((sp) => {
            return [...getPaymentContent(sp)];
          }) as any[]),
        ],
      },
      layout: "lightHorizontalLines",
    },
    [
      (payment.studentPayments ?? []).map((m) => {
        return { ...detail, text: `${m.studentFirstName} ${m.studentLastName} - ${getMonth(m)}` };
      }),
    ],
    payment.comment != null && payment.comment.length > 0 ?
      { ...detail, text: t("payments.commentPrefix", { comment: payment.comment }) } : {},
    [
      { text: t("common.total"), ...detail, alignment: "right" },
      {
        text: `${numberToMadPdf(payment.totalAmount)}`,
        ...header,
        alignment: "right",
      },
      {
        text: t("payments.paidOnSummary", {
          date: dateToString(payment.date),
          term: paymentTermTypeToString(payment.paymentTerm),
          paidBy: payment.paidBy,
        }),
        ...subHeader,
        alignment: "right",
      },
    ],
    pdfFooter(institution),
  ];

  var docDefinition = {
    info: {
      title: 'awesome Document',
      author: 'john doe',
      subject: 'subject of document',
      keywords: 'keywords for document',
      creationDate: '2021-08-20 10:35:06.7700000 +00:00'
    },
    pageSize: institution?.receiptFormat,
    pageOrientation: institution?.receiptOrientation,
    defaultStyle: {
      fontSize: 9,
    },
    content: content,
  };

  return (
    <div>
      {isMobile || isTablet ? (
        <DefaultButton
          text="PDF"
          onClick={() => pdfMake.createPdf(docDefinition).open()}
          iconProps={{ iconName: "PDF" }}
        />
      ) : (
        <DefaultButton
          split
          text="PDF"
          menuProps={{
            items: [
              {
                key: "download",
                text: t("common.download"),
                iconProps: { iconName: "DownloadDocument" },
                onClick: () =>
                  pdfMake
                    .createPdf(docDefinition)
                    .download(`${t("payments.singular")} ${payment.paymentNumber}.pdf`),
              },
              {
                key: "print",
                text: t("pdf.print"),
                iconProps: { iconName: "Print" },
                onClick: () => pdfMake.createPdf(docDefinition).print(),
              },
            ],
          }}
          onClick={() => pdfMake.createPdf(docDefinition).open()}
          iconProps={{ iconName: "PDF" }}
        />
      )}
    </div>
  );
}
